// src/pages/auth/Login.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { loginWithEmail, loginWithGoogle } from '../../services/firebase/authService';
import { auth } from '../../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const checkUserExists = async (email) => {
    try {
      const userDoc = doc(db, 'users', email);
      const userSnap = await getDoc(userDoc);
      return userSnap.exists();
    } catch (error) {
      console.error('User check error:', error);
      return false;
    }
  };

  // Normal giriş
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await loginWithEmail(email, password);
      
      if (!result.success) {
        throw result.error;
      }

      const userExists = await checkUserExists(email);
      if (!userExists) {
        await auth.signOut();
        setError('Hesap bulunamadı. Lütfen önce kayıt olun.');
        return;
      }

      // Yönlendirilecek sayfa varsa oraya, yoksa dashboard'a git
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });

    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Email veya şifre hatalı.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin.');
      } else {
        setError('Giriş yapılırken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Google ile giriş
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
  
    try {
      const result = await loginWithGoogle();
      
      if (!result.success) {
        throw result.error;
      }

      const userExists = await checkUserExists(result.user.email);
      if (!userExists) {
        await auth.signOut();
        setError('Hesap bulunamadı. Lütfen önce kayıt olun.');
        return;
      }

      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });

    } catch (error) {
      console.error('Google login error:', error);
      setError('Google ile giriş sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E23] flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8 bg-[#151934] p-8 rounded-lg">
        <div>
          <h2 className="text-3xl font-bold text-center text-white mb-8">Login</h2>
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 rounded-lg transition-colors"
        >
          <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.99 10.187c0-.82-.069-1.417-.216-2.037H10.2v3.698h5.62c-.113.92-.725 2.303-2.084 3.233l-.02.124 3.028 2.292.21.02c1.926-1.738 3.037-4.299 3.037-7.33z" fill="#4285F4"/>
            <path d="M10.2 19.931c2.753 0 5.064-.886 6.753-2.414l-3.218-2.436c-.862.587-2.017.997-3.536.997-2.704 0-4.972-1.772-5.799-4.267l-.119.01-3.148 2.38-.041.112c1.677 3.256 5.07 5.618 9.108 5.618z" fill="#34A853"/>
            <path d="M4.4 11.811a6.707 6.707 0 0 1-.38-2.185c0-.76.144-1.497.38-2.185v-.118L1.218 5.006l-.105.05A10.51 10.51 0 0 0 0 9.626c0 1.635.37 3.184 1.114 4.57l3.286-2.385z" fill="#FBBC05"/>
            <path d="M10.2 3.359c1.917 0 3.207.807 3.944 1.48l2.87-2.73C15.253.784 12.953 0 10.199 0 6.16 0 2.768 2.362 1.09 5.617l3.285 2.385C5.228 5.131 7.496 3.36 10.2 3.36z" fill="#EA4335"/>
          </svg>
          Sign in with Google
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#151934] text-gray-400">or</span>
          </div>
        </div>

        <form onSubmit={handleLogin} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Email"
                required
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-300"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/forgot-password" className="text-sm text-blue-500 hover:text-blue-400">
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#151934] disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-gray-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-blue-500 hover:text-blue-400">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;